import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import { EditingStyle, UpdateEditingStyleInput } from '../../types';
import { Translate } from '../../../../shared/LanguageService';
import { useEditEditingStyleForm } from '../state/useEditEditingStyleForm';
import { descriptionFieldValue } from '../../editingStyleDescription';
import { whiteNotchedOutline } from '../../../../shared/whiteNotchedOutline';

type EditEditingStyleModalProps = {
    open: boolean;
    onClose: () => void;
    editingStyle: EditingStyle | null;
    onEditSave: (id: number, input: UpdateEditingStyleInput) => void;
};

type EditEditingStyleFormProps = {
    onClose: () => void;
    editingStyle: EditingStyle;
    onEditSave: (id: number, input: UpdateEditingStyleInput) => void;
};

function EditEditingStyleForm({ onClose, editingStyle, onEditSave }: EditEditingStyleFormProps) {
    const { isDirty, handleChange, handleSubmit } = useEditEditingStyleForm({
        editingStyle,
        onEditSave,
        onClose
    });

    return (
        <form onChange={handleChange} onSubmit={handleSubmit}>
            <DialogTitle>
                {Translate('editing-styles/modal/title-edit', { name: editingStyle.name })}
            </DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1, ...whiteNotchedOutline }}>
                    <TextField
                        name="name"
                        label={Translate('editing-styles/modal/field/name')}
                        defaultValue={editingStyle.name}
                        required
                        fullWidth
                    />
                    <TextField
                        name="description"
                        label={Translate('editing-styles/modal/field/description')}
                        defaultValue={descriptionFieldValue(editingStyle.description)}
                        multiline
                        minRows={3}
                        fullWidth
                    />
                    <FormControlLabel
                        control={<Checkbox name="isActive" defaultChecked={editingStyle.isActive} />}
                        label={Translate('editing-styles/modal/field/is-active')}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button type="button" onClick={onClose}>
                    {Translate('editing-styles/modal/button/cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={!isDirty}>
                    {Translate('editing-styles/modal/button/save')}
                </Button>
            </DialogActions>
        </form>
    );
}

export function EditEditingStyleModal({
    open,
    onClose,
    editingStyle,
    onEditSave
}: EditEditingStyleModalProps) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            {editingStyle != null ? (
                <EditEditingStyleForm
                    key={editingStyle.id}
                    onClose={onClose}
                    editingStyle={editingStyle}
                    onEditSave={onEditSave}
                />
            ) : null}
        </Dialog>
    );
}
